import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { differenceInDays } from "date-fns";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { supabase } from "@/integrations/supabase/client";
import { Eye, MousePointer, TrendingUp, Clock, Sparkles, Rocket } from "lucide-react";

interface FeaturedData {
  feature_type: string;
  end_date: string;
  total_impressions: number;
  total_clicks: number;
}

export function FeaturedStats() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<FeaturedData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('featured_professionals')
        .select('feature_type, end_date, total_impressions, total_clicks')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .gte('end_date', new Date().toISOString())
        .maybeSingle();

      if (error) {
        console.error('Error loading featured stats:', error);
        return;
      }
      
      setStats(data);
    } catch (error) {
      console.error('Error loading featured stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) return null;

  const daysRemaining = Math.max(0, differenceInDays(new Date(stats.end_date), new Date()));
  const ctr = stats.total_impressions > 0
    ? ((stats.total_clicks / stats.total_impressions) * 100).toFixed(2)
    : '0.00';

  const isSpotlight = stats.feature_type === 'spotlight';
  const titleKey = isSpotlight ? 'dashboard.spotlightStatus' : 'dashboard.socialBoostStatus';

  return (
    <div className="space-y-4">
      <h3 className="font-heading font-bold text-xl flex items-center gap-2">
        {isSpotlight ? (
          <Sparkles className="w-5 h-5 text-amber-500" />
        ) : (
          <Rocket className="w-5 h-5 text-violet-500" />
        )}
        {t(titleKey)}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <BrutalCard className="p-4">
          <div className="flex items-center gap-3">
            <Eye className="w-8 h-8 text-primary" />
            <div>
              <div className="text-2xl font-heading font-bold">
                {stats.total_impressions}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('dashboard.impressions')}
              </div>
            </div>
          </div>
        </BrutalCard>

        <BrutalCard className="p-4">
          <div className="flex items-center gap-3">
            <MousePointer className="w-8 h-8 text-secondary" />
            <div>
              <div className="text-2xl font-heading font-bold">
                {stats.total_clicks}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('dashboard.clicks')}
              </div>
            </div>
          </div>
        </BrutalCard>

        <BrutalCard className="p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-success" />
            <div>
              <div className="text-2xl font-heading font-bold">
                {ctr}%
              </div>
              <div className="text-xs text-muted-foreground">
                {t('dashboard.ctr')}
              </div>
            </div>
          </div>
        </BrutalCard>

        <BrutalCard className={`p-4 ${isSpotlight ? 'bg-amber-50' : 'bg-violet-50'}`}>
          <div className="flex items-center gap-3">
            <Clock className={`w-8 h-8 ${isSpotlight ? 'text-amber-600' : 'text-violet-600'}`} />
            <div>
              <div className={`text-2xl font-heading font-bold ${isSpotlight ? 'text-amber-600' : 'text-violet-600'}`}>
                {daysRemaining}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('dashboard.daysRemaining')}
              </div>
            </div>
          </div>
        </BrutalCard>
      </div>
    </div>
  );
}
